import React, { useState, useEffect, useRef, useCallback } from "react";
// A callback is the instant the agent meant, shown in their zone and language (guide §6.8k).
import { bizDayjs, dateTimeText, todayDay, typedInstant } from "../../../utils/times";
import { useTranslation } from "react-i18next";

import { useColorMode } from "../../../theme/ColorModeContext";
import { EmptyState } from "../../../design-system";
import {
  Box, Typography, Button, TextField, Chip, Avatar, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, InputAdornment, IconButton,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, LinearProgress,
  Tooltip, List, ListItem, ListItemText, Checkbox, Menu, TablePagination,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { setCredentials, clearMustChange } from "../../../actions/authSlice";
import { fetchDispositions, tagCall } from "../agents/dispositionsSlice";
import HandoffCard from "../handoff/HandoffCard";
import { getCallerBriefing, getCurrentCallHandoff, saveHandoffOutcome } from "../handoff/handoffApi";
import { ContactFacts, OpenItems, WhatsAppRow } from "../handoff/BriefingParts";
import { hasOpenItems, mergedHistory } from "../handoff/briefingMeta";
import axiosInstance from "../../../services/axiosInstance";

import LockResetOutlinedIcon          from "@mui/icons-material/LockResetOutlined";
import PhoneInTalkOutlinedIcon         from "@mui/icons-material/PhoneInTalkOutlined";
import CheckCircleIcon                 from "@mui/icons-material/CheckCircle";
import PhoneMissedOutlinedIcon         from "@mui/icons-material/PhoneMissedOutlined";
import PhoneDisabledOutlinedIcon       from "@mui/icons-material/PhoneDisabledOutlined";
import PhoneOutlinedIcon               from "@mui/icons-material/PhoneOutlined";
import TimerOutlinedIcon               from "@mui/icons-material/TimerOutlined";
import VisibilityOutlinedIcon          from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon       from "@mui/icons-material/VisibilityOffOutlined";
import RefreshOutlinedIcon             from "@mui/icons-material/RefreshOutlined";
import PlayCircleOutlineIcon           from "@mui/icons-material/PlayCircleOutline";
import StopCircleOutlinedIcon          from "@mui/icons-material/StopCircleOutlined";
import ConfirmationNumberOutlinedIcon  from "@mui/icons-material/ConfirmationNumberOutlined";
import NoteAddOutlinedIcon             from "@mui/icons-material/NoteAddOutlined";
import TaskAltOutlinedIcon             from "@mui/icons-material/TaskAltOutlined";
import PersonAddOutlinedIcon           from "@mui/icons-material/PersonAddOutlined";
import TodayOutlinedIcon               from "@mui/icons-material/TodayOutlined";
import AssignmentOutlinedIcon          from "@mui/icons-material/AssignmentOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import HistoryOutlinedIcon             from "@mui/icons-material/HistoryOutlined";
import PersonOutlinedIcon              from "@mui/icons-material/PersonOutlined";
import EditOutlinedIcon                from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon               from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon          from "@mui/icons-material/LocationOnOutlined";
import CloseIcon                       from "@mui/icons-material/Close";
import LabelOutlinedIcon               from "@mui/icons-material/LabelOutlined";
import BusinessOutlinedIcon            from "@mui/icons-material/BusinessOutlined";
import TrendingUpOutlinedIcon          from "@mui/icons-material/TrendingUpOutlined";
import AddOutlinedIcon                 from "@mui/icons-material/AddOutlined";
import CheckOutlinedIcon               from "@mui/icons-material/CheckOutlined";
import { usePhone } from "../../phone/phoneContext";
import LiveNowBand from "../live/LiveNowBand";
import CallButton from "../../phone/CallButton";

// ── design tokens ─────────────────────────────────────────────────────────────
const BRAND         = "#4F46E5";
const ROWS_PER_PAGE = 25;
const LABEL_SX    = {
  fontSize: "0.68rem", fontWeight: 600, color: "text.secondary",
  textTransform: "uppercase", letterSpacing: ".07em",
};
// ── helpers ───────────────────────────────────────────────────────────────────
function fmtSecs(s) {
  if (!s) return "-";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

// The business's today (4.6a): the browser's day was wrong for an agent outside the business's zone.
function todayStr() {
  return todayDay();
}

function daysAgoStr(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

const STATUS_MAP = {
  ANSWER:           { label: "Answered",    labelKey: "statusAnswered",    bg: alpha("#22C55E", 0.1),  color: "#059669" },
  NOANSWER:         { label: "Missed",      labelKey: "statusMissed",      bg: alpha("#EF4444", 0.1),  color: "#DC2626" },
  BUSY:             { label: "Busy",        labelKey: "statusBusy",        bg: alpha("#F59E0B", 0.1),  color: "#D97706" },
  CHANUNAVAIL:      { label: "Unavailable", labelKey: "statusUnavailable", bg: alpha("#6B7280", 0.1),  color: "#6B7280" },
  VOICEMAIL:        { label: "Voicemail",   labelKey: "statusVoicemail",   bg: alpha("#3B82F6", 0.1),  color: "#2563EB" },
  DIALING:          { label: "Dialing",     labelKey: "statusDialing",     bg: alpha("#8B5CF6", 0.1),  color: "#7C3AED" },
  TRANSFER_DIALING: { label: "Transferred", labelKey: "statusTransferred", bg: alpha("#8B5CF6", 0.1),  color: "#7C3AED" },
  INVALIDARGS:      { label: "Error",       labelKey: "statusError",       bg: alpha("#EF4444", 0.1),  color: "#DC2626" },
};

function StatusChip({ st }) {
  const { t } = useTranslation("agentDash");
  const c = STATUS_MAP[st] || { label: st || "-", bg: alpha("#9CA3AF", 0.1), color: "#6B7280" };
  const chipLabel = c.labelKey ? t(c.labelKey) : c.label;
  return (
    <Chip label={chipLabel} size="small"
      sx={{ height: 22, fontSize: "0.67rem", fontWeight: 700, background: c.bg, color: c.color,
            borderRadius: 1, "& .MuiChip-label": { px: 1 } }} />
  );
}

const TICKET_STATUS_COLORS = {
  open:        { bg: alpha("#3B82F6", 0.1), color: "#2563EB" },
  in_progress: { bg: alpha("#F59E0B", 0.1), color: "#D97706" },
  resolved:    { bg: alpha("#22C55E", 0.1), color: "#059669" },
  closed:      { bg: alpha("#9CA3AF", 0.1), color: "#6B7280" },
};

function TicketStatusChip({ status }) {
  const c = TICKET_STATUS_COLORS[status] || TICKET_STATUS_COLORS.open;
  const label = status ? status.replace("_", " ") : "open";
  return (
    <Chip label={label} size="small"
      sx={{ height: 22, fontSize: "0.67rem", fontWeight: 700, textTransform: "capitalize",
            background: c.bg, color: c.color, borderRadius: 1, "& .MuiChip-label": { px: 1 } }} />
  );
}

const PRIORITY_COLORS = {
  low:    "#6B7280",
  normal: "#2563EB",
  high:   "#D97706",
  urgent: "#DC2626",
};

function PriorityChip({ priority }) {
  const color = PRIORITY_COLORS[priority] || PRIORITY_COLORS.normal;
  return (
    <Chip label={priority || "normal"} size="small"
      sx={{ height: 22, fontSize: "0.67rem", fontWeight: 700, textTransform: "capitalize",
            background: alpha(color, 0.1), color, borderRadius: 1, "& .MuiChip-label": { px: 1 } }} />
  );
}

// ── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ icon: Icon, children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: "text.disabled" }} />}
      <Typography sx={LABEL_SX}>{children}</Typography>
    </Box>
  );
}

// ── Force change password ─────────────────────────────────────────────────────
function ForceChangePassword() {
  const { t } = useTranslation("agentDash");
  const dispatch = useDispatch();
  const user     = useSelector((s) => s.auth.user);
  const [pw,     setPw]     = useState("");
  const [pw2,    setPw2]    = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err,    setErr]    = useState("");
  const [saving, setSaving] = useState(false);
  const [done,   setDone]   = useState(false);

  const submit = async () => {
    if (pw.length < 6)  return setErr(t("pwMinLength"));
    if (pw !== pw2)      return setErr(t("pwMismatch"));
    setSaving(true); setErr("");
    try {
      const res = await axiosInstance.post("/SubAccounts/change-password", { newPassword: pw });
      dispatch(setCredentials({ token: res.data.token }));
      dispatch(clearMustChange());
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.message || t("pwChangeFailed"));
    } finally { setSaving(false); }
  };

  if (done)
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Box sx={{ width: 56, height: 56, borderRadius: "50%", background: alpha("#22C55E", 0.12),
                   display: "inline-flex", alignItems: "center", justifyContent: "center", mb: 1.5 }}>
          <CheckCircleIcon sx={{ fontSize: 28, color: "#059669" }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{t("pwUpdated")}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          {t("pwLoadingDash")}
        </Typography>
        <CircularProgress size={18} sx={{ color: BRAND, mt: 2 }} />
      </Box>
    );

  return (
    <Box sx={{ minHeight: "80vh", display: "flex", flexDirection: "column",
               alignItems: "center", justifyContent: "center", px: 2 }}>
      <Box sx={{ maxWidth: 400, width: "100%" }}>
        <Box sx={{ textAlign: "center", mb: 3.5 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: "50%",
                     background: alpha(BRAND, 0.1), border: `1px solid ${alpha(BRAND, 0.2)}`,
                     display: "inline-flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
            <LockResetOutlinedIcon sx={{ fontSize: 24, color: BRAND }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "-0.025em" }}>
            {t("pwSetTitle")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}
            dangerouslySetInnerHTML={{ __html: t("pwSetSubtitle", { name: `<strong>${user?.name}</strong>` }) }}
          />
        </Box>
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>{err}</Alert>}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label={t("pwNewLabel")} type={showPw ? "text" : "password"} size="medium" fullWidth
            value={pw} onChange={(e) => setPw(e.target.value)}
            InputProps={{ endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPw((s) => !s)}>
                  {showPw ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )}}
          />
          <TextField
            label={t("pwConfirmLabel")} type={showPw ? "text" : "password"} size="medium" fullWidth
            value={pw2} onChange={(e) => setPw2(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
          <Button variant="contained" fullWidth size="large" onClick={submit} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <LockResetOutlinedIcon />}
            sx={{ fontWeight: 700, py: 1.5, background: BRAND,
                  "&:hover": { background: alpha(BRAND, 0.85) },
                  "&:disabled": { background: alpha(BRAND, 0.4) } }}>
            {saving ? t("pwSaving") : t("pwSetContinue")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color }) {
  const { mode } = useColorMode();
  const isLight  = mode === "light";
  return (
    <Box sx={{
      p: 2.5, borderRadius: "16px",
      background: "var(--surface-card)",
      
      
      border: "1px solid var(--line-100)",
      boxShadow: "none",
      display: "flex", flexDirection: "column", gap: 1.5,
      transition: "box-shadow .22s ease",
      "&:hover": { boxShadow: `var(--shadow-card), 0 0 14px ${alpha(color, 0.14)}` },
      "@media (prefers-reduced-transparency: reduce)": {
        background: isLight ? "rgba(242,238,250,0.97)" : "rgba(22,17,38,0.97)",
        backdropFilter: "none",
        WebkitBackdropFilter: "none",
      },
    }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={LABEL_SX}>{label}</Typography>
        <Box sx={{ width: 32, height: 32, borderRadius: "10px", background: "var(--surface-card)", boxShadow: "none",
                   display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon sx={{ fontSize: 15, color }} />
        </Box>
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: "1.875rem", lineHeight: 1,
                        letterSpacing: "-0.03em", color: "text.primary" }}>
        {value ?? "-"}
      </Typography>
    </Box>
  );
}

// ── Create Ticket Dialog ──────────────────────────────────────────────────────
function CreateTicketDialog({ open, onClose, onCreated, prefillCallerId, prefillUniqueId }) {
  const { t } = useTranslation("agentDash");
  const [subject,  setSubject]  = useState("");
  const [priority, setPriority] = useState("normal");
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState("");

  useEffect(() => {
    if (open) { setSubject(t("ticketDefaultSubject")); setPriority("normal"); setErr(""); }
  }, [open, t]);

  const submit = async () => {
    if (!subject.trim()) return setErr(t("ticketSubjectRequired"));
    setSaving(true); setErr("");
    try {
      const res = await axiosInstance.post("/SubAccounts/tickets", {
        subject: subject.trim(), priority,
        callerId: prefillCallerId || null,
        uniqueId: prefillUniqueId || null,
      });
      onCreated(res.data);
    } catch (e) {
      setErr(e.response?.data?.message || t("ticketCreateFailed"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: alpha(BRAND, 0.1),
                   display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ConfirmationNumberOutlinedIcon sx={{ fontSize: 17, color: BRAND }} />
        </Box>
        {t("ticketNewTitle")}
      </DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>{err}</Alert>}
        {prefillCallerId && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, background: alpha(BRAND, 0.05),
                     border: "1px solid", borderColor: alpha(BRAND, 0.12),
                     display: "flex", alignItems: "center", gap: 1 }}>
            <PhoneOutlinedIcon sx={{ fontSize: 15, color: BRAND }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND }}>
              {prefillCallerId}
            </Typography>
          </Box>
        )}
        <TextField label={t("ticketSubjectLabel")} fullWidth size="small" value={subject}
          onChange={(e) => setSubject(e.target.value)} sx={{ mb: 2 }} />
        <FormControl fullWidth size="small">
          <InputLabel>{t("ticketPriorityLabel")}</InputLabel>
          <Select value={priority} label={t("ticketPriorityLabel")} onChange={(e) => setPriority(e.target.value)}>
            {["low","normal","high","urgent"].map(p => (
              <MenuItem key={p} value={p} sx={{ textTransform: "capitalize" }}>{t(`ticketPrio${p.charAt(0).toUpperCase() + p.slice(1)}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: "text.secondary" }}>{t("ticketCancel")}</Button>
        <Button variant="contained" onClick={submit} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <ConfirmationNumberOutlinedIcon />}
          sx={{ background: BRAND, "&:hover": { background: alpha(BRAND, 0.85) }, fontWeight: 700 }}>
          {t("ticketCreate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Ticket Detail Dialog ──────────────────────────────────────────────────────
function TicketDetailDialog({ ticketId, open, onClose, team, currentUser }) {
  const { t } = useTranslation("agentDash");
  const [ticket,    setTicket]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [noteText,  setNoteText]  = useState("");
  const [noteType,  setNoteType]  = useState("note");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskTo,    setTaskTo]    = useState("");
  const [taskDue,   setTaskDue]   = useState("");
  const [err,       setErr]       = useState("");
  const [saving,    setSaving]    = useState("");
  const [statusVal, setStatusVal] = useState("");

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/SubAccounts/tickets/${ticketId}`);
      setTicket(res.data);
      setStatusVal(res.data.status);
    } catch { setErr(t("ticketLoadFailed")); }
    finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const updateStatus = async (s) => {
    setStatusVal(s);
    try {
      const res = await axiosInstance.patch(`/SubAccounts/tickets/${ticketId}`, { status: s });
      setTicket((prev) => ({ ...prev, status: res.data.status }));
    } catch { load(); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSaving("note");
    try {
      const res = await axiosInstance.post(`/SubAccounts/tickets/${ticketId}/notes`, {
        content: noteText.trim(), noteType, authorName: currentUser?.name,
      });
      setTicket((prev) => ({ ...prev, notes: [...(prev.notes || []), res.data] }));
      setNoteText("");
    } catch (e) { setErr(e.response?.data?.message || t("genFailed")); }
    finally { setSaving(""); }
  };

  const addTask = async () => {
    if (!taskTitle.trim()) return;
    setSaving("task");
    const member = team.find((m) => String(m.id) === String(taskTo));
    try {
      const res = await axiosInstance.post(`/SubAccounts/tickets/${ticketId}/tasks`, {
        title: taskTitle.trim(),
        assignedToId:   member?.id   || null,
        assignedToName: member ? `${member.firstName} ${member.lastName}`.trim() : null,
        dueDate: taskDue || null,
      });
      setTicket((prev) => ({ ...prev, tasks: [...(prev.tasks || []), res.data] }));
      setTaskTitle(""); setTaskTo(""); setTaskDue("");
    } catch (e) { setErr(e.response?.data?.message || t("genFailed")); }
    finally { setSaving(""); }
  };

  const markDone = async (taskId) => {
    try {
      const res = await axiosInstance.patch(`/SubAccounts/tasks/${taskId}/done`);
      setTicket((prev) => ({ ...prev, tasks: prev.tasks.map((tk) => tk.id === res.data.id ? res.data : tk) }));
    } catch { /* ignore */ }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <AssignmentOutlinedIcon sx={{ color: BRAND, fontSize: 20 }} />
        {t("ticketDetailTitle", { id: ticketId })}
        {ticket && <TicketStatusChip status={ticket.status} />}
        {ticket && <PriorityChip priority={ticket.priority} />}
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>{err}</Alert>}

        {ticket && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>{ticket.subject}</Typography>
            {ticket.callerId && (
              <Box sx={{ mb: 1.5 }}>
                {ticket.callerName && (
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                    {ticket.callerName}
                  </Typography>
                )}
                <Typography sx={{ fontSize: ticket.callerName ? '0.75rem' : '0.875rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                  {ticket.callerId}
                </Typography>
              </Box>
            )}

            {/* Status control */}
            <Box sx={{ display: "flex", gap: 0.75, mb: 2, flexWrap: "wrap" }}>
              {["open","in_progress","resolved","closed"].map((s) => {
                const c = TICKET_STATUS_COLORS[s];
                return (
                  <Chip key={s} label={s.replace("_", " ")} size="small" onClick={() => updateStatus(s)}
                    sx={{ textTransform: "capitalize", fontWeight: 700, fontSize: "0.72rem",
                          cursor: "pointer", borderRadius: 1,
                          background: statusVal === s ? c.bg : "transparent",
                          color: statusVal === s ? c.color : "text.secondary",
                          border: "1px solid",
                          borderColor: statusVal === s ? c.color : "rgba(197,189,212,0.45)",
                          "&:hover": { background: alpha(c.color, 0.08) } }} />
                );
              })}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Notes */}
            <Typography sx={{ ...LABEL_SX, display: "block", mb: 1 }}>{t("ticketNoteSection")}</Typography>
            {(ticket.notes || []).length === 0 && (
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
                {t("ticketNoNotes")}
              </Typography>
            )}
            {(ticket.notes || []).map((n) => (
              <Box key={n.id} sx={{ p: 1.5, background: alpha(BRAND, 0.04), borderRadius: 1.5, mb: 1,
                                    border: "1px solid", borderColor: alpha(BRAND, 0.1) }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{n.authorName || t("ticketAuthorFallback")}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {n.noteType} · {n.createdAt ? bizDayjs(n.createdAt).format("HH:mm") : ""}
                  </Typography>
                </Box>
                <Typography variant="body2">{n.content}</Typography>
              </Box>
            ))}

            {/* Add note */}
            <Box sx={{ display: "flex", gap: 1, mt: 1, mb: 2 }}>
              <TextField size="small" placeholder={t("ticketNotePlaceholder")} fullWidth multiline maxRows={3}
                value={noteText} onChange={(e) => setNoteText(e.target.value)} />
              <FormControl size="small" sx={{ minWidth: 108 }}>
                <Select value={noteType} onChange={(e) => setNoteType(e.target.value)}>
                  <MenuItem value="note">{t("ticketNoteTypeNote")}</MenuItem>
                  <MenuItem value="followup">{t("ticketNoteTypeFollowup")}</MenuItem>
                  <MenuItem value="disposition">{t("ticketNoteTypeDisposition")}</MenuItem>
                </Select>
              </FormControl>
              <Button variant="contained" onClick={addNote} disabled={saving === "note"}
                sx={{ background: BRAND, "&:hover": { background: alpha(BRAND, 0.85) },
                      minWidth: 38, px: 1 }}>
                {saving === "note"
                  ? <CircularProgress size={14} color="inherit" />
                  : <NoteAddOutlinedIcon fontSize="small" />}
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Tasks */}
            <Typography sx={{ ...LABEL_SX, display: "block", mb: 1 }}>{t("ticketTaskSection")}</Typography>
            {(ticket.tasks || []).length === 0 && (
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
                {t("ticketNoTasks")}
              </Typography>
            )}
            <List dense disablePadding>
              {(ticket.tasks || []).map((tk) => (
                <ListItem key={tk.id} disableGutters
                  secondaryAction={
                    tk.status !== "done" ? (
                      <Tooltip title={t("ticketMarkDone")}>
                        <IconButton size="small" onClick={() => markDone(tk.id)}>
                          <TaskAltOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : <CheckCircleIcon fontSize="small" sx={{ color: "#059669" }} />
                  }>
                  <Checkbox checked={tk.status === "done"}
                    onChange={() => tk.status !== "done" && markDone(tk.id)}
                    size="small" sx={{ p: 0.5, mr: 0.5 }} />
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600,
                        textDecoration: tk.status === "done" ? "line-through" : "none",
                        color: tk.status === "done" ? "text.secondary" : "text.primary" }}>
                        {tk.title}
                      </Typography>
                    }
                    secondary={tk.assignedToName ? t("ticketAssigned", { name: tk.assignedToName }) : undefined}
                  />
                </ListItem>
              ))}
            </List>

            {/* Add task */}
            <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
              <TextField size="small" placeholder={t("ticketTaskPlaceholder")} sx={{ flex: 2, minWidth: 140 }}
                value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} />
              <FormControl size="small" sx={{ flex: 1, minWidth: 110 }}>
                <InputLabel>{t("ticketAssignTo")}</InputLabel>
                <Select value={taskTo} label={t("ticketAssignTo")} onChange={(e) => setTaskTo(e.target.value)}>
                  <MenuItem value="">{t("ticketAnyAssignee")}</MenuItem>
                  {(team || []).map((m) => (
                    <MenuItem key={m.id} value={String(m.id)}>
                      {m.firstName} {m.lastName} ({m.extension})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" type="date" label={t("ticketDueLabel")} sx={{ width: 140 }}
                value={taskDue} onChange={(e) => setTaskDue(e.target.value)}
                InputLabelProps={{ shrink: true }} />
              <Button variant="contained" onClick={addTask} disabled={saving === "task"}
                sx={{ background: BRAND, "&:hover": { background: alpha(BRAND, 0.85) }, px: 1 }}>
                {saving === "task"
                  ? <CircularProgress size={14} color="inherit" />
                  : <PersonAddOutlinedIcon fontSize="small" />}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} variant="outlined"
          sx={{ borderColor: "rgba(197,189,212,0.5)", color: "text.primary", "&:hover": { borderColor: BRAND } }}>
          {t("ticketClose")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Recording player cell ─────────────────────────────────────────────────────
function RecordingCell({ uniqueId, hasRecording, audioRef }) {
  const { t } = useTranslation("agentDash");
  const [loading,  setLoading]  = useState(false);
  const [playing,  setPlaying]  = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  if (!hasRecording)
    return <Typography variant="caption" sx={{ color: "text.disabled" }}>-</Typography>;

  const toggle = async () => {
    if (playing && audioRef.current) { audioRef.current.pause(); setPlaying(false); return; }
    if (audioUrl && audioRef.current) {
      audioRef.current.src = audioUrl; audioRef.current.play(); setPlaying(true); return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/SubAccounts/recording?uniqueId=${encodeURIComponent(uniqueId)}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      setAudioUrl(url);
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.onended = () => setPlaying(false);
        await audioRef.current.play();
        setPlaying(true);
      }
    } catch { /* recording unavailable */ }
    finally { setLoading(false); }
  };

  return (
    <Tooltip title={playing ? t("recStop") : t("recPlay")}>
      <span>
        <IconButton size="small" onClick={toggle} disabled={loading}
          sx={{ color: playing ? "#DC2626" : BRAND, "&:hover": { background: alpha(BRAND, 0.06) } }}>
          {loading ? <CircularProgress size={15} />
            : playing ? <StopCircleOutlinedIcon sx={{ fontSize: 18 }} />
            : <PlayCircleOutlineIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

// ── Client Profile Dialog ─────────────────────────────────────────────────────
function ClientInfoDialog({ open, callerId, initialProfile, onClose, onSaved }) {
  const { t } = useTranslation("agentDash");
  const [form, setForm] = useState({ name: "", email: "", address: "", notes: "", phones: [] });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        name:    initialProfile?.name    ?? "",
        email:   initialProfile?.email   ?? "",
        address: initialProfile?.address ?? "",
        notes:   initialProfile?.notes   ?? "",
        phones:  Array.isArray(initialProfile?.phones) ? initialProfile.phones : [],
      });
      setErr("");
    }
  }, [open, initialProfile]);

  const addPhone    = () => setForm(f => ({ ...f, phones: [...f.phones, { label: "Mobile", number: "" }] }));
  const removePhone = (i) => setForm(f => ({ ...f, phones: f.phones.filter((_, idx) => idx !== i) }));
  const updatePhone = (i, field, val) =>
    setForm(f => ({ ...f, phones: f.phones.map((ph, idx) => idx === i ? { ...ph, [field]: val } : ph) }));

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const r = await axiosInstance.put("/SubAccounts/caller-profile", {
        callerId, ...form, phones: form.phones.filter(p => p.number.trim()),
      });
      onSaved(r.data); onClose();
    } catch (e) { setErr(e.response?.data?.message || t("contSaveFailed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: alpha(BRAND, 0.1),
                   display: "flex", alignItems: "center", justifyContent: "center" }}>
          <PersonOutlinedIcon sx={{ fontSize: 17, color: BRAND }} />
        </Box>
        {t("contEditTitle")}
        <Box component="span" sx={{ ml: 0.5, px: 1, py: 0.25, borderRadius: 1,
                                    background: alpha(BRAND, 0.07), fontSize: "0.78rem",
                                    fontWeight: 600, color: BRAND, fontFamily: "monospace" }}>
          {callerId}
        </Box>
      </DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>{err}</Alert>}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          <TextField label={t("contEditName")} size="small" fullWidth value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <TextField label={t("contEditEmail")} type="email" size="small" fullWidth value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <TextField label={t("contEditAddress")} size="small" fullWidth value={form.address}
            onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography sx={LABEL_SX}>{t("contEditPhones")}</Typography>
              <Button size="small" onClick={addPhone}
                sx={{ fontSize: "0.75rem", color: BRAND, fontWeight: 600,
                      "&:hover": { background: alpha(BRAND, 0.06) } }}>
                {t("contAddPhone")}
              </Button>
            </Box>
            {form.phones.length === 0 ? (
              <Typography variant="caption" sx={{ color: "text.disabled" }}>
                {t("contNoPhones")}
              </Typography>
            ) : form.phones.map((ph, i) => (
              <Box key={i} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
                <Select size="small" value={ph.label} sx={{ minWidth: 90 }}
                  onChange={e => updatePhone(i, "label", e.target.value)}>
                  {[
                    { value: "Mobile", label: t("phoneLabelMobile") },
                    { value: "Work",   label: t("phoneLabelWork") },
                    { value: "Home",   label: t("phoneLabelHome") },
                    { value: "Other",  label: t("phoneLabelOther") },
                  ].map(({ value, label }) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </Select>
                <TextField size="small" value={ph.number} placeholder={t("contEditNumPlaceholder")} sx={{ flex: 1 }}
                  onChange={e => updatePhone(i, "number", e.target.value)} />
                <IconButton size="small" onClick={() => removePhone(i)}
                  sx={{ "&:hover": { color: "#DC2626" } }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
          <TextField label={t("contEditNotes")} multiline rows={3} size="small" fullWidth value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: "text.secondary" }}>{t("contCancel")}</Button>
        <Button variant="contained" onClick={save} disabled={saving}
          sx={{ background: BRAND, "&:hover": { background: alpha(BRAND, 0.85) }, fontWeight: 700 }}>
          {saving ? t("contSaving") : t("contSave")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Live Call Panel (screen pop) ──────────────────────────────────────────────
function LiveCallPanel({
  callInfo, history, historyLoading,
  onCreateTicket, onOpenTicket,
  onProfileSaved, onTicketStatusChange, onNoteAdded,
  audioRef,
  crmContact, crmContactLoading, oncallPerm,
  onCrmContactCreated,
  handoff = null,
  // WHO IS CALLING, ACROSS EVERY CHANNEL (4.5, the owner's design 2026-10-26): the CRM's view of them, their WhatsApp
  // conversations beside the calls, and what is open for them — cut to what this agent may see (server/ai/callerBriefing.js).
  briefing = null,
}) {
  const { t } = useTranslation("agentDash");
  const { t: tH } = useTranslation("handoff");
  const user      = useSelector((s) => s.auth.user);
  const { mode }  = useColorMode();
  const isLight   = mode === "light";
  const callerId      = callInfo?.callerId         || "";
  const callerDisplay = callInfo?.callerDisplayName || "";
  const callState     = callInfo?.state             || "";

  const [profileDialog,    setProfileDialog]    = useState(false);
  const [quickNote,        setQuickNote]        = useState("");
  const [quickNoteType,    setQuickNoteType]    = useState("note");
  const [addingNote,       setAddingNote]       = useState(false);
  const [crmNoteOpen,      setCrmNoteOpen]      = useState(false);
  const [crmNoteText,      setCrmNoteText]      = useState("");
  const [crmNoteSaving,    setCrmNoteSaving]    = useState(false);
  const [addContactOpen,   setAddContactOpen]   = useState(false);
  const [newContactName,   setNewContactName]   = useState("");
  const [addContactSaving, setAddContactSaving] = useState(false);

  const canAct = oncallPerm === "actions" || oncallPerm === "full";

  const submitCrmNote = async () => {
    if (!crmNoteText.trim() || !crmContact?.id) return;
    setCrmNoteSaving(true);
    try {
      await axiosInstance.post("/crm/v2/activities", {
        type: "note", direction: "inbound",
        contactId: crmContact.id,
        subject: `Call note — ${callerId}`,
        content: crmNoteText.trim(),
        completedAt: new Date().toISOString(),
      });
      setCrmNoteText(""); setCrmNoteOpen(false);
    } catch {} finally { setCrmNoteSaving(false); }
  };

  const submitAddContact = async () => {
    if (!newContactName.trim()) return;
    setAddContactSaving(true);
    try {
      const nameParts = newContactName.trim().split(" ");
      const r = await axiosInstance.post("/crm/v2/contacts", {
        firstName: nameParts[0], lastName: nameParts.slice(1).join(" ") || "",
        callerId,
      });
      onCrmContactCreated?.(r.data);
      setAddContactOpen(false); setNewContactName("");
    } catch {} finally { setAddContactSaving(false); }
  };

  const STATE_CONFIG = {
    ringing: { accent: "#D97706", dot: "#F59E0B", icon: NotificationsActiveOutlinedIcon, label: t("callStateRinging") },
    calling: { accent: "#2563EB", dot: "#3B82F6", icon: PhoneOutlinedIcon,               label: t("callStateCalling") },
    active:  { accent: "#059669", dot: "#22C55E", icon: PhoneInTalkOutlinedIcon,          label: t("callStateActive")  },
    ended:   { accent: "#6B7280", dot: "#9CA3AF", icon: PhoneMissedOutlinedIcon,          label: t("callStateEnded")   },
  };
  const cfg = STATE_CONFIG[callState] || STATE_CONFIG.active;
  const CallIcon = cfg.icon;

  const totalCalls    = history?.totalCalls    || 0;
  const totalAnswered = history?.totalAnswered || 0;
  const prevCalls     = history?.calls         || [];
  const openTicket    = history?.openTicket    || null;
  const profile       = history?.profile       || null;

  const submitQuickNote = async () => {
    if (!quickNote.trim() || !openTicket) return;
    setAddingNote(true);
    try {
      const r = await axiosInstance.post(`/SubAccounts/tickets/${openTicket.id}/notes`, {
        content: quickNote.trim(), noteType: quickNoteType, authorName: user?.name,
      });
      onNoteAdded?.(openTicket.id, r.data);
      setQuickNote("");
    } catch {} finally { setAddingNote(false); }
  };

  return (
    <>
      {/* WHAT THE ASSISTANT TOLD US (4.5, guide §6.10): the call it handed over — shown while it rings, to every agent rung. */}
      {handoff && <Box sx={{ mb: 2 }}><HandoffCard handoff={handoff} /></Box>}
      <Box sx={{
        mb: 2, borderRadius: "16px", overflow: "hidden",
        background: "var(--surface-card)",
        
        
        border: "1px solid var(--line-200)",
        boxShadow: `var(--shadow-card), inset 3px 0 0 ${cfg.accent}`,
        "@media (prefers-reduced-transparency: reduce)": {
          background: isLight ? "rgba(242,238,250,0.97)" : "rgba(22,17,38,0.97)",
          backdropFilter: "none",
          WebkitBackdropFilter: "none",
        },
      }}>
        {/* ── Panel header ── */}
        <Box sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center",
                   justifyContent: "space-between", flexWrap: "wrap", gap: 1,
                   borderBottom: `1px solid rgba(197,189,212,0.4)`,
                   background: alpha(cfg.accent, 0.04) }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: "50%",
              background: alpha(cfg.accent, 0.12),
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <CallIcon sx={{ fontSize: 15, color: cfg.accent }} />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{
                  width: 6, height: 6, borderRadius: "50%", background: cfg.dot,
                  animation: (callState === "ringing" || callState === "active")
                    ? "livePulse 1.4s ease-in-out infinite" : "none",
                  "@keyframes livePulse": {
                    "0%,100%": { opacity: 1, transform: "scale(1)" },
                    "50%":     { opacity: 0.4, transform: "scale(0.85)" },
                  },
                }} />
                <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: cfg.accent,
                                   textTransform: "uppercase", letterSpacing: ".06em" }}>
                  {cfg.label}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "text.primary" }}>
              {profile?.name || briefing?.contact?.name || callerDisplay || callerId || t("unknownCaller")}
            </Typography>
            {(profile?.name || briefing?.contact?.name || callerDisplay) && (
              <Typography sx={{ fontSize: "0.78rem", color: "text.secondary",
                                 fontFamily: "monospace" }}>
                {callerId}
              </Typography>
            )}
            {totalCalls > 0 && (
              <Chip size="small" label={t("callerTotalCalls", { count: totalCalls })}
                sx={{ height: 20, fontSize: "0.65rem", fontWeight: 600, borderRadius: 1,
                      background: alpha(BRAND, 0.08), color: BRAND,
                      "& .MuiChip-label": { px: 0.875 } }} />
            )}
            {totalAnswered > 0 && (
              <Chip size="small" label={t("callerTotalAnswered", { count: totalAnswered })}
                sx={{ height: 20, fontSize: "0.65rem", fontWeight: 600, borderRadius: 1,
                      background: alpha("#059669", 0.1), color: "#059669",
                      "& .MuiChip-label": { px: 0.875 } }} />
            )}
          </Box>
        </Box>

        {/* ── CRM contact strip (on-call panel) ── */}
        {oncallPerm !== "none" && (callState === "active" || callState === "calling") && (
          <Box sx={{
            px: 2, py: 1.25,
            borderBottom: "1px solid rgba(197,189,212,0.4)",
            background: crmContact ? alpha(BRAND, 0.03) : "transparent",
            display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
          }}>
            {crmContactLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={14} sx={{ color: BRAND }} />
                <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>{t("crmLookingUp")}</Typography>
              </Box>
            ) : crmContact ? (
              <>
                {/* Avatar */}
                <Box sx={{
                  width: 32, height: 32, borderRadius: "10px", flexShrink: 0,
                  background: alpha(BRAND, 0.12), border: `1px solid ${alpha(BRAND, 0.2)}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, color: BRAND }}>
                    {(crmContact.firstName || "?")[0].toUpperCase()}
                  </Typography>
                </Box>
                {/* Name + company + deal */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "text.primary" }}>
                      {[crmContact.firstName, crmContact.lastName].filter(Boolean).join(" ")}
                    </Typography>
                    {crmContact.companyName && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.375 }}>
                        <BusinessOutlinedIcon sx={{ fontSize: 11, color: "text.disabled" }} />
                        <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>{crmContact.companyName}</Typography>
                      </Box>
                    )}
                    {(crmContact.openDeals || []).length > 0 && (
                      <Chip
                        size="small"
                        icon={<TrendingUpOutlinedIcon sx={{ fontSize: "10px !important" }} />}
                        label={crmContact.openDeals[0].stageName || t("crmInPipeline")}
                        sx={{ height: 18, fontSize: "0.62rem", fontWeight: 600, borderRadius: "6px",
                              background: alpha("#059669", 0.1), color: "#059669",
                              "& .MuiChip-label": { px: 0.75 }, "& .MuiChip-icon": { ml: 0.5, color: "#059669" } }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: "0.68rem", color: "text.disabled", mt: 0.1 }}>
                    {t("crmInCRMStatus", { status: crmContact.status || "lead" })}
                    {(crmContact.openDeals || []).length > 0 && t("crmOpenDeals", { count: crmContact.openDeals.length })}
                  </Typography>
                </Box>
                {/* Quick actions */}
                {canAct && (
                  <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
                    <Tooltip title={t("crmLogNote")}>
                      <Button size="small" variant="outlined"
                        onClick={() => setCrmNoteOpen((o) => !o)}
                        startIcon={<NoteAddOutlinedIcon sx={{ fontSize: 13 }} />}
                        sx={{ height: 28, fontSize: "0.7rem", fontWeight: 600, textTransform: "none",
                              borderColor: BRAND, color: BRAND, "&:hover": { background: alpha(BRAND, 0.05) } }}>
                        {t("crmLogNoteBtn")}
                      </Button>
                    </Tooltip>
                    <Tooltip title={t("crmCreateTicket")}>
                      <Button size="small" variant="outlined"
                        onClick={() => onCreateTicket(callerId)}
                        startIcon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: 13 }} />}
                        sx={{ height: 28, fontSize: "0.7rem", fontWeight: 600, textTransform: "none",
                              borderColor: "#D97706", color: "#D97706", "&:hover": { background: alpha("#D97706", 0.05) } }}>
                        {t("crmCreateTicketBtn")}
                      </Button>
                    </Tooltip>
                  </Box>
                )}
              </>
            ) : (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <PersonAddOutlinedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                  <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                    {t("crmNotInCRM", { callerId })}
                  </Typography>
                </Box>
                {canAct && !addContactOpen && (
                  <Button size="small" variant="outlined"
                    startIcon={<AddOutlinedIcon sx={{ fontSize: 13 }} />}
                    onClick={() => setAddContactOpen(true)}
                    sx={{ height: 28, fontSize: "0.7rem", fontWeight: 600, textTransform: "none",
                          borderColor: BRAND, color: BRAND, "&:hover": { background: alpha(BRAND, 0.05) } }}>
                    {t("crmAddToCRM")}
                  </Button>
                )}
                {canAct && addContactOpen && (
                  <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
                    <TextField
                      size="small" autoFocus
                      placeholder={t("crmFullNamePlaceholder")}
                      value={newContactName}
                      onChange={(e) => setNewContactName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && submitAddContact()}
                      sx={{ width: 160, "& input": { fontSize: "0.8rem", py: 0.5 } }}
                    />
                    <Button size="small" variant="contained"
                      disabled={!newContactName.trim() || addContactSaving}
                      onClick={submitAddContact}
                      sx={{ height: 32, minWidth: 0, px: 1.5, background: BRAND, "&:hover": { background: alpha(BRAND, 0.85) } }}>
                      {addContactSaving ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : <CheckOutlinedIcon sx={{ fontSize: 16 }} />}
                    </Button>
                    <Button size="small" onClick={() => { setAddContactOpen(false); setNewContactName(""); }}
                      sx={{ height: 32, minWidth: 0, px: 1, color: "text.secondary" }}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </Button>
                  </Box>
                )}
              </>
            )}

            {/* Inline note input */}
            {crmNoteOpen && crmContact && (
              <Box sx={{ width: "100%", mt: 0.25, display: "flex", gap: 0.75, alignItems: "flex-start" }}>
                <TextField
                  size="small" multiline rows={2} fullWidth autoFocus
                  placeholder={t("crmNotePlaceholder")}
                  value={crmNoteText}
                  onChange={(e) => setCrmNoteText(e.target.value)}
                  sx={{ "& textarea": { fontSize: "0.8rem" } }}
                />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Button size="small" variant="contained"
                    disabled={!crmNoteText.trim() || crmNoteSaving}
                    onClick={submitCrmNote}
                    sx={{ height: 32, minWidth: 0, px: 1.5, background: BRAND, fontSize: "0.72rem",
                          fontWeight: 600, textTransform: "none", "&:hover": { background: alpha(BRAND, 0.85) } }}>
                    {crmNoteSaving ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : t("crmNoteSave")}
                  </Button>
                  <Button size="small" onClick={() => { setCrmNoteOpen(false); setCrmNoteText(""); }}
                    sx={{ height: 32, minWidth: 0, px: 1, color: "text.secondary", fontSize: "0.72rem", textTransform: "none" }}>
                    {t("crmNoteCancel")}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* ── 3-column body ── */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>

          {/* Col 1: Client info */}
          <Box sx={{ width: { xs: "100%", md: "27%" }, p: 2,
                     borderRight: { md: "1px solid rgba(197,189,212,0.4)" },
                     borderBottom: { xs: "1px solid rgba(197,189,212,0.4)", md: "none" } }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
              <SectionLabel icon={PersonOutlinedIcon}>{t("crmClientInfo")}</SectionLabel>
              <Tooltip title={t("crmEditProfile")}>
                <IconButton size="small" onClick={() => setProfileDialog(true)}
                  sx={{ width: 24, height: 24, "&:hover": { background: alpha(BRAND, 0.06) } }}>
                  <EditOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                </IconButton>
              </Tooltip>
            </Box>

            {historyLoading && !profile ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {callerDisplay && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <PersonOutlinedIcon sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{callerDisplay}</Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <PhoneOutlinedIcon sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: "monospace" }}>{callerId}</Typography>
                </Box>
                <CircularProgress size={12} sx={{ color: BRAND, mt: 0.5 }} />
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {profile?.name ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <PersonOutlinedIcon sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{profile.name}</Typography>
                  </Box>
                ) : callerDisplay ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <PersonOutlinedIcon sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{callerDisplay}</Typography>
                  </Box>
                ) : briefing?.contact ? null : (
                  <Typography variant="caption" sx={{ color: "text.disabled", fontStyle: "italic" }}>
                    {t("crmNoProfile")}
                  </Typography>
                )}
                {briefing?.contact && (
                  <ContactFacts contact={{ ...briefing.contact, email: profile?.email ? null : briefing.contact.email }}
                    showName={!profile?.name && !callerDisplay} />
                )}
                {profile?.email && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <EmailOutlinedIcon sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>{profile.email}</Typography>
                  </Box>
                )}
                {profile?.address && (
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                    <LocationOnOutlinedIcon sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0, mt: 0.1 }} />
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>{profile.address}</Typography>
                  </Box>
                )}
                {Array.isArray(profile?.phones) && profile.phones.map((ph, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <PhoneOutlinedIcon sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {ph.label}: {ph.number}
                    </Typography>
                  </Box>
                ))}
                {profile?.notes && (
                  <Box sx={{ mt: 0.5, p: 1, borderRadius: 1.5, background: alpha(BRAND, 0.04),
                             border: "1px solid", borderColor: alpha(BRAND, 0.1) }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                      {profile.notes}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Col 2: Call history */}
          <Box sx={{ flex: 1, p: 2, borderRight: { md: "1px solid rgba(197,189,212,0.4)" },
                     borderBottom: { xs: "1px solid rgba(197,189,212,0.4)", md: "none" } }}>
            <SectionLabel icon={HistoryOutlinedIcon}>{briefing?.allowed?.whatsapp ? tH("briefing.historyAllChannels") : t("crmCallHistory")}</SectionLabel>

            {historyLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={14} sx={{ color: BRAND }} />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>{t("crmCallsLoading")}</Typography>
              </Box>
            ) : prevCalls.length === 0 && !(briefing?.whatsapp?.length) ? (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {t("crmNoPrevCalls")}
              </Typography>
            ) : (
              <Box>
                {mergedHistory(prevCalls, briefing?.whatsapp ?? []).map((row, i, rows) => {
                  if (row.type === "whatsapp") return <WhatsAppRow key={row.key} wa={row.wa} last={i === rows.length - 1} />;
                  const c = row.call;
                  const cs = STATUS_MAP[c.DialStatus] || { label: c.DialStatus, color: "#6B7280", bg: alpha("#6B7280", 0.1) };
                  const hasRec = !!(c.RecordingPath) && c.DialStatus === "ANSWER";
                  return (
                    <Box key={row.key} sx={{
                      display: "flex", alignItems: "center", gap: 1, py: 0.75,
                      borderBottom: i < rows.length - 1 ? "1px solid rgba(197,189,212,0.4)" : "none",
                    }}>
                      <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 100,
                                                           fontSize: "0.7rem", fontFamily: "monospace" }}>
                        {dateTimeText(c.startTime)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 48,
                                                           fontSize: "0.7rem" }}>
                        {c.SectionName || c.sectionName || "-"}
                      </Typography>
                      <StatusChip st={c.DialStatus} />
                      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                        {fmtSecs(c.Duration || c.duration)}
                      </Typography>
                      {hasRec && (
                        <RecordingCell uniqueId={c.UniqueId} hasRecording audioRef={audioRef} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Col 3: Active ticket */}
          <Box sx={{ width: { xs: "100%", md: 272 }, p: 2 }}>
            <SectionLabel icon={AssignmentOutlinedIcon}>{hasOpenItems(briefing?.open) ? tH("briefing.openNow") : t("crmTicketSection")}</SectionLabel>
            <OpenItems open={briefing?.open} />

            {historyLoading ? (
              <CircularProgress size={16} sx={{ color: BRAND }} />
            ) : openTicket ? (
              <Box>
                {/* Ticket meta */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: BRAND }}>
                    #{openTicket.id}
                  </Typography>
                  <PriorityChip priority={openTicket.priority} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, lineHeight: 1.3 }}>
                  {openTicket.subject}
                </Typography>

                {/* Status selector */}
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1.25 }}>
                  {["open","in_progress","resolved","closed"].map((s) => {
                    const isCurrent = openTicket.status === s;
                    const sc = TICKET_STATUS_COLORS[s] || { bg: "#f5f5f5", color: "#555" };
                    return (
                      <Chip key={s} label={s.replace("_"," ")} size="small" clickable
                        onClick={() => onTicketStatusChange?.(openTicket.id, s)}
                        sx={{ height: 22, fontSize: "0.65rem", textTransform: "capitalize",
                              fontWeight: isCurrent ? 700 : 500, borderRadius: 1,
                              background: isCurrent ? sc.bg : "transparent",
                              color: isCurrent ? sc.color : "text.secondary",
                              border: `1px solid ${isCurrent ? sc.color : "rgba(197,189,212,0.45)"}`,
                              "&:hover": { background: alpha(sc.color, 0.08) },
                              "& .MuiChip-label": { px: 0.875 } }} />
                    );
                  })}
                </Box>

                {/* Last note preview */}
                {Array.isArray(openTicket.notes) && openTicket.notes.length > 0 && (
                  <Box sx={{ mb: 1, p: 1, borderRadius: 1.5, background: alpha(BRAND, 0.04),
                             border: "1px solid", borderColor: alpha(BRAND, 0.1) }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary",
                                                         display: "block" }}>
                      {openTicket.notes.at(-1).authorName || t("ticketAuthorFallback")}:
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {openTicket.notes.at(-1).content}
                    </Typography>
                  </Box>
                )}

                {/* Quick note */}
                <FormControl size="small" fullWidth sx={{ mb: 0.75 }}>
                  <Select value={quickNoteType} onChange={e => setQuickNoteType(e.target.value)}
                    sx={{ "& .MuiSelect-select": { py: 0.6, fontSize: "0.75rem" } }}>
                    <MenuItem value="note">{t("ticketNoteTypeNote")}</MenuItem>
                    <MenuItem value="followup">{t("ticketNoteTypeFollowup")}</MenuItem>
                    <MenuItem value="disposition">{t("ticketNoteTypeDisposition")}</MenuItem>
                  </Select>
                </FormControl>
                <TextField size="small" multiline rows={2} fullWidth placeholder={t("crmQuickNotePlaceholder")}
                  value={quickNote} onChange={e => setQuickNote(e.target.value)}
                  sx={{ mb: 1, "& textarea": { fontSize: "0.8rem" } }} />
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <Button size="small" variant="contained"
                    disabled={!quickNote.trim() || addingNote}
                    onClick={submitQuickNote}
                    sx={{ flex: 1, height: 28, fontSize: "0.72rem", fontWeight: 700,
                          background: BRAND, "&:hover": { background: alpha(BRAND, 0.85) } }}>
                    {addingNote ? t("crmSavingNote") : t("crmSaveNote")}
                  </Button>
                  <Button size="small" variant="outlined"
                    onClick={() => onOpenTicket(openTicket.id)}
                    sx={{ flex: 1, height: 28, fontSize: "0.72rem", fontWeight: 600,
                          borderColor: BRAND, color: BRAND,
                          "&:hover": { background: alpha(BRAND, 0.05) } }}>
                    {t("crmFullDetails")}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
                  {t("crmNoOpenTicket")}
                </Typography>
                <Button size="small" variant="outlined" fullWidth
                  startIcon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: 15 }} />}
                  onClick={() => onCreateTicket(callerId)}
                  sx={{ height: 32, fontSize: "0.78rem", fontWeight: 700,
                        borderColor: BRAND, color: BRAND,
                        "&:hover": { background: alpha(BRAND, 0.05) } }}>
                  {t("crmCreateTicketForCaller")}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <ClientInfoDialog
        open={profileDialog}
        callerId={callerId}
        initialProfile={profile}
        onClose={() => setProfileDialog(false)}
        onSaved={onProfileSaved}
      />
    </>
  );
}

// ── Shared table header cell ──────────────────────────────────────────────────
function TH({ children, sx: sxProp, ...props }) {
  return (
    <TableCell
      sx={{
        ...LABEL_SX, py: 1.25, px: 1.75,
        background: (theme) => alpha(theme.palette.text.primary, 0.028),
        borderBottom: `1px solid rgba(197,189,212,0.4)`,
        whiteSpace: "nowrap",
        ...sxProp,
      }}
      {...props}
    >
      {children}
    </TableCell>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────
export default function AgentDashboard({ onIncomingCall }) {
  const { t } = useTranslation("agentDash");
  const dispatch   = useDispatch();
  const user       = useSelector((s) => s.auth.user);
  const mustChange = useSelector((s) => s.auth.user?.mustChangePassword);
  const dispositions = useSelector((s) => s.dispositions?.labels || []);
  const { mode }   = useColorMode();
  const isLight    = mode === "light";

  const [fromDate,  setFromDate]  = useState(todayStr);
  const [toDate,    setToDate]    = useState(todayStr);
  const [tab,       setTab]       = useState(0);
  const [callsPage, setCallsPage] = useState(0);
  const [perf,      setPerf]      = useState(null);
  const [perfLoad,  setPerfLoad]  = useState(false);
  const [calls,     setCalls]     = useState([]);
  const [callsLoad, setCallsLoad] = useState(false);
  const [tickets,   setTickets]   = useState([]);
  const [tickLoad,  setTickLoad]  = useState(false);
  const [team,      setTeam]      = useState([]);

  const [createTicket,   setCreateTicket]   = useState({ open: false, callerId: "", uniqueId: "" });
  const [viewTicketId,   setViewTicketId]   = useState(null);
  const [activeCallInfo, setActiveCallInfo] = useState(null);
  // THE HAND-OFF PACKET of the call ringing or answered (4.5): looked for while it rings, kept for the wrap-up after it ends.
  const [handoff, setHandoff] = useState(null);
  const handoffRef = useRef(null);
  // WHO IS CALLING, ACROSS EVERY CHANNEL (4.5, the owner's design): read as it rings, read again on answer (the call may have
  // moved things), for the ring popup's short briefing and the panel.
  const [briefing, setBriefing] = useState(null);
  const [callerHistory,  setCallerHistory]  = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [crmContact,     setCrmContact]     = useState(null);
  const [crmContactLoading, setCrmContactLoading] = useState(false);
  const [oncallPerm,     setOncallPerm]     = useState("actions");
  const [statusMenu,     setStatusMenu]     = useState({ anchorEl: null, ticketId: null });
  const [dispDialog,     setDispDialog]     = useState(null); // null | { caller, duration, status, crmContactId }
  const [dispSelected,   setDispSelected]   = useState("");
  const [dispNote,       setDispNote]       = useState("");
  const [dispSaving,     setDispSaving]     = useState(false);

  // ── Agent status (queue availability) ────────────────────────────────────

  // ── Callbacks ─────────────────────────────────────────────────────────────
  const [callbacks,      setCallbacks]      = useState([]);
  const [cbLoading,      setCbLoading]      = useState(false);
  const [cbForm,         setCbForm]         = useState({ callerNum: "", callerName: "", scheduledAt: "", notes: "" });
  const [cbFormOpen,     setCbFormOpen]     = useState(false);
  const [cbSaving,       setCbSaving]       = useState(false);

  const audioRef = useRef(new Audio());

  // ── data loaders ─────────────────────────────────────────────────────────
  const loadPerf = useCallback(async () => {
    setPerfLoad(true);
    try {
      const r = await axiosInstance.get(`/SubAccounts/my-performance?fromDate=${fromDate}&toDate=${toDate}`);
      setPerf(r.data);
    } catch { setPerf(null); }
    finally { setPerfLoad(false); }
  }, [fromDate, toDate]);

  const loadCalls = useCallback(async () => {
    setCallsLoad(true);
    const view = tab === 1 ? "answered" : "attempts";
    try {
      const r = await axiosInstance.get(`/SubAccounts/my-calls?fromDate=${fromDate}&toDate=${toDate}&view=${view}`);
      setCalls(r.data.items || []);
    } catch { setCalls([]); }
    finally { setCallsLoad(false); }
  }, [fromDate, toDate, tab]);

  const loadTickets = useCallback(async () => {
    setTickLoad(true);
    try { const r = await axiosInstance.get("/SubAccounts/tickets"); setTickets(r.data || []); }
    catch { setTickets([]); }
    finally { setTickLoad(false); }
  }, []);

  const loadTeam = useCallback(async () => {
    try { const r = await axiosInstance.get("/SubAccounts/team-directory"); setTeam(r.data || []); }
    catch { setTeam([]); }
  }, []);

  const loadCallbacks = useCallback(async () => {
    setCbLoading(true);
    try { const r = await axiosInstance.get("/agent/callbacks"); setCallbacks(r.data || []); }
    catch { setCallbacks([]); } finally { setCbLoading(false); }
  }, []);

  const createCallback = useCallback(async () => {
    if (!cbForm.callerNum.trim() || !cbForm.scheduledAt) return;
    setCbSaving(true);
    try {
      const r = await axiosInstance.post("/agent/callbacks", { ...cbForm, scheduledAt: typedInstant(cbForm.scheduledAt) });
      setCallbacks((prev) => [r.data, ...prev].sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt)));
      setCbForm({ callerNum: "", callerName: "", scheduledAt: "", notes: "" });
      setCbFormOpen(false);
    } catch {} finally { setCbSaving(false); }
  }, [cbForm]);

  const doneCallback = useCallback(async (id) => {
    try {
      await axiosInstance.patch(`/agent/callbacks/${id}/done`);
      setCallbacks((prev) => prev.filter((c) => c.id !== id));
    } catch {}
  }, []);

  const deleteCallback = useCallback(async (id) => {
    try {
      await axiosInstance.delete(`/agent/callbacks/${id}`);
      setCallbacks((prev) => prev.filter((c) => c.id !== id));
    } catch {}
  }, []);

  useEffect(() => { loadPerf(); }, [loadPerf]);
  useEffect(() => {
    if (tab === 0 || tab === 1) loadCalls();
    else if (tab === 2) loadTickets();
    else if (tab === 3) loadCallbacks();
  }, [tab, loadCalls, loadTickets, loadCallbacks]);
  useEffect(() => { loadTeam(); }, [loadTeam]);
  useEffect(() => { setCallsPage(0); }, [tab, fromDate, toDate]);

  // ── caller history (screen-pop) ───────────────────────────────────────────
  const loadCallerHistory = useCallback(async (callerId) => {
    if (!callerId) return;
    setHistoryLoading(true);
    try {
      const r = await axiosInstance.get(`/SubAccounts/caller-history?callerId=${encodeURIComponent(callerId)}`);
      setCallerHistory(r.data);
    } catch { setCallerHistory({ calls: [], tickets: [], totalCalls: 0, totalAnswered: 0 }); }
    finally { setHistoryLoading(false); }
  }, []);

  // ── CRM contact lookup for screen-pop ────────────────────────────────────
  const loadCrmContact = useCallback(async (phone) => {
    if (!phone) return;
    setCrmContactLoading(true);
    try {
      const r = await axiosInstance.get(`/crm/v2/contacts/by-phone?phone=${encodeURIComponent(phone)}`);
      setCrmContact(r.data.contact || null);
    } catch { setCrmContact(null); }
    finally { setCrmContactLoading(false); }
  }, []);

  // ── Load own permission profile (once) ───────────────────────────────────
  useEffect(() => {
    axiosInstance.get("/crm/v2/my-perms")
      .then(r => { if (r.data?.perms?.oncall_panel) setOncallPerm(r.data.perms.oncall_panel); })
      .catch(() => {});
  }, []);

  // ── the assistant's hand-off packet (4.5) ─────────────────────────────────
  // Written the moment the assistant hands the call over, so usually there at the first look; looked for again while the phone
  // rings in case the ring came first. A call the assistant did not hand over simply has none.
  useEffect(() => {
    const st = activeCallInfo?.state;
    if (!st || st === "ended" || handoff) return undefined;
    let alive = true;
    let tries = 0;
    let timer = null;
    const look = async () => {
      try {
        const r = await getCurrentCallHandoff(activeCallInfo?.callerId);
        if (alive && r?.handoff) { setHandoff(r.handoff); handoffRef.current = r.handoff; return; }
      } catch { /* the next look */ }
      if (alive && ++tries < 20) timer = setTimeout(look, 2000);
    };
    timer = setTimeout(look, 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [activeCallInfo?.state, activeCallInfo?.callerId, handoff]);

  useEffect(() => {
    const st = activeCallInfo?.state;
    const caller = activeCallInfo?.callerId;
    if (!caller || !["ringing", "calling", "active"].includes(st)) return undefined;
    let alive = true;
    getCallerBriefing(caller).then((r) => { if (alive) setBriefing(r?.briefing ?? null); }).catch(() => {});
    return () => { alive = false; };
  }, [activeCallInfo?.state, activeCallInfo?.callerId]);

  // ── soft phone handlers ───────────────────────────────────────────────────
  const handleActiveCall = useCallback((info) => {
    if (info?.state === "ringing") {
      onIncomingCall?.();
      // A new call: the last one's packet and briefing are not this one's.
      handoffRef.current = null;
      setHandoff(null);
      setBriefing(null);
    }
    if (info?.state === "ended") {
      setActiveCallInfo(null);
      setHandoff(null);
      setBriefing(null);
      setCallerHistory(null);
      setCrmContact(null);
      setTimeout(() => { loadPerf(); loadCalls(); }, 2000);
    } else {
      setActiveCallInfo(info);
      if (info?.callerId && !callerHistory) loadCallerHistory(info.callerId);
      if (info?.state === "active") {
        loadPerf(); loadCalls();
        loadCrmContact(info.callerId);
      }
    }
  }, [onIncomingCall, loadCallerHistory, callerHistory, loadPerf, loadCalls, loadCrmContact]);

  const handleOpenTicketFromCall = useCallback((callerId) => {
    setCreateTicket({ open: true, callerId, uniqueId: "" });
  }, []);

  // THE PHONE IS THE APP'S (Stage 4.8f.4b, PhoneProvider.jsx — docked beside every page): this page hears the call in focus (its CRM
  // panel) and the calls that end (the wrap-up), and gives the phone what its ringing card shows.
  const phone = usePhone();
  const callHandlersRef = useRef({});

  const handleCallEnded = useCallback(({ caller, duration, status }) => {
    if (!caller || status === "missed" || status === "failed") return;
    if (dispositions.length === 0) dispatch(fetchDispositions());
    setDispSelected("");
    setDispNote("");
    setDispDialog((prev) => {
      const crmContactId = crmContact?.id || null;
      return { caller, duration, status, crmContactId, handoff: handoffRef.current };
    });
  }, [dispatch, dispositions.length, crmContact]);
  callHandlersRef.current = { onFocus: handleActiveCall, onEnded: handleCallEnded };
  useEffect(() => phone.subscribe((ev) => {
    if (ev.type === "focus") callHandlersRef.current.onFocus?.(ev.info);
    else if (ev.type === "ended") callHandlersRef.current.onEnded?.(ev.info);
  }), [phone.subscribe]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    phone.setRingContext({ briefing, handoff, callerProfile: callerHistory?.profile ?? null, onOpenTicket: handleOpenTicketFromCall });
  }, [phone.setRingContext, briefing, handoff, callerHistory, handleOpenTicketFromCall]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDispSave = useCallback(async () => {
    if (!dispDialog) return;
    setDispSaving(true);
    try {
      const packet = dispDialog.handoff?.status === "open" ? dispDialog.handoff : null;
      if (dispSelected) {
        const uniqueId = packet?.callId
          ?? (await axiosInstance.get(`/crm/latest-call-id?callerId=${encodeURIComponent(dispDialog.caller)}`)).data.uniqueId;
        const chosen = dispositions.find(d => d.id === dispSelected);
        if (chosen) await dispatch(tagCall({ uniqueId, dispositionId: chosen.id, label: chosen.label, color: chosen.color }));
      }
      if (packet && (dispSelected || dispNote.trim())) {
        await saveHandoffOutcome(packet.id, { dispositionId: dispSelected || null, note: dispNote.trim() || null }).catch(() => {});
      }
      // The packet writes its note on the timeline where it knows the customer; otherwise the note goes as it always has.
      if (dispNote.trim() && dispDialog.crmContactId && !(packet?.contactId)) {
        await axiosInstance.post("/crm/v2/activities", {
          type: "note",
          direction: "inbound",
          contactId: dispDialog.crmContactId,
          subject: `Post-call note — ${dispDialog.caller}`,
          content: dispNote.trim(),
          duration: dispDialog.duration || null,
          completedAt: new Date().toISOString(),
        });
      }
      setDispDialog(null);
      setDispNote("");
    } catch {
      setDispDialog(null);
      setDispNote("");
    } finally {
      setDispSaving(false);
    }
  }, [dispSelected, dispNote, dispDialog, dispositions, dispatch]);

  const updateTicketStatus = useCallback(async (ticketId, status) => {
    try {
      await axiosInstance.patch(`/SubAccounts/tickets/${ticketId}`, { status });
      setTickets(prev => prev.map(tk => tk.id === ticketId ? { ...tk, status } : tk));
      setCallerHistory(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          openTicket: prev.openTicket?.id === ticketId
            ? { ...prev.openTicket, status } : prev.openTicket,
          tickets: (prev.tickets || []).map(tk => tk.id === ticketId ? { ...tk, status } : tk),
        };
      });
    } catch {}
    setStatusMenu({ anchorEl: null, ticketId: null });
  }, []);

  const handleNoteAdded = useCallback((ticketId, note) => {
    setCallerHistory(prev => {
      if (!prev || prev.openTicket?.id !== ticketId) return prev;
      return { ...prev, openTicket: { ...prev.openTicket, notes: [...(prev.openTicket.notes || []), note] } };
    });
  }, []);

  const handleProfileSaved = useCallback((profile) => {
    setCallerHistory(prev => prev ? { ...prev, profile } : prev);
  }, []);

  if (mustChange) return <ForceChangePassword />;

  const answerPct  = perf?.answerRate ?? 0;
  const rateColor  = answerPct >= 70 ? "#059669" : answerPct >= 40 ? "#D97706" : "#DC2626";
  const openTicketCount = tickets.filter(tk => tk.status === "open" || tk.status === "in_progress").length;

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%" }}>

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                 flexWrap: "wrap", gap: 1.5, mb: 3 }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", letterSpacing: "-0.025em",
                             lineHeight: 1.2, color: "text.primary" }}>
            {t("pageDashTitle")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.875, mt: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
              {user?.name}
            </Typography>
            <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "text.disabled" }} />
            <Box sx={{ display: "inline-flex", alignItems: "center", px: 0.875, py: 0.2,
                       borderRadius: 1, background: alpha(BRAND, 0.08),
                       border: "1px solid", borderColor: alpha(BRAND, 0.15) }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: BRAND,
                                 fontFamily: "monospace", letterSpacing: ".02em" }}>
                ext {user?.extension}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Date range + refresh */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0,
                     borderRadius: "12px",
                     background: "var(--surface-card)",
                     border: "1px solid var(--line-100)",
                     boxShadow: "none",
                     overflow: "hidden",
                     transition: "box-shadow .18s",
                     "&:hover": { boxShadow: "var(--shadow-card)" } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.25, py: 0.5 }}>
              <TodayOutlinedIcon sx={{ color: "text.disabled", fontSize: 15 }} />
              <TextField type="date" size="small" value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); if (e.target.value > toDate) setToDate(e.target.value); }}
                inputProps={{ max: todayStr() }}
                sx={{ width: 124, "& input": { fontSize: "0.8rem", p: 0, cursor: "pointer" },
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "& .MuiInputBase-root": { px: 0 } }}
              />
            </Box>
            <Box sx={{ width: 1, height: 28, bgcolor: "divider" }} />
            <Box sx={{ display: "flex", alignItems: "center", px: 1.25, py: 0.5 }}>
              <TextField type="date" size="small" value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                inputProps={{ min: fromDate, max: todayStr() }}
                sx={{ width: 124, "& input": { fontSize: "0.8rem", p: 0, cursor: "pointer" },
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "& .MuiInputBase-root": { px: 0 } }}
              />
            </Box>
          </Box>
          <Tooltip title={t("tooltipRefresh")}>
            <IconButton size="small"
              onClick={() => { loadPerf(); if (tab < 2) loadCalls(); else if (tab === 2) loadTickets(); else loadCallbacks(); }}
              sx={{ borderRadius: "10px", width: 34, height: 34,
                   background: "var(--surface-card)",
                   border: "1px solid var(--line-100)",
                   boxShadow: "none",
                   "&:hover": { boxShadow: "var(--shadow-card)" },
                   "&:active": { boxShadow: "inset 0 1px 3px var(--line-200)" } }}>
              <RefreshOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Live now — polls /api/live/summary every 2.5s, scoped to this agent's section */}
      <LiveNowBand />

      {/* Your status (available / on a break / do not disturb / offline) is set in the phone's header now (4.8f.4a) — one place,
          beside the calls it decides; the engine keeps calls from ringing an agent who is away (4.8f.3). */}

      {/* ── Two-column layout ────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start", width: "100%",
                 flexDirection: { xs: "column", lg: "row" } }}>

        {/* ── Left column ─────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* Stats grid */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                     gap: 1.5, mb: 2.5 }}>
            {perfLoad ? (
              <Box sx={{ gridColumn: "1 / -1", display: "flex", alignItems: "center",
                         justifyContent: "center", p: 4 }}>
                <CircularProgress size={24} sx={{ color: BRAND }} />
              </Box>
            ) : (
              <>
                <StatCard icon={PhoneInTalkOutlinedIcon} label={t("statTotalAttempts")}
                  value={perf?.totalAttempts ?? 0} color={BRAND} />
                <StatCard icon={CheckCircleIcon} label={t("statAnsweredCalls")}
                  value={perf?.answeredCalls ?? 0} color="#059669" />
                <StatCard icon={PhoneMissedOutlinedIcon} label={t("statMissed")}
                  value={perf?.missed ?? 0} color="#DC2626" />
                <StatCard icon={PhoneDisabledOutlinedIcon} label={t("statBusy")}
                  value={perf?.busy ?? 0} color="#D97706" />
                <StatCard icon={TimerOutlinedIcon} label={t("statAvgDuration")}
                  value={fmtSecs(perf?.avgDuration)} color="#2563EB" />

                {/* Answer rate card */}
                <Box sx={{
                  p: 2.5, borderRadius: "16px",
                  background: "var(--surface-card)",
                  
                  
                  border: "1px solid var(--line-100)",
                  boxShadow: "none",
                  display: "flex", flexDirection: "column", gap: 1.5,
                  transition: "box-shadow .22s ease",
                  "&:hover": { boxShadow: `var(--shadow-card), 0 0 14px ${alpha(rateColor, 0.14)}` },
                  "@media (prefers-reduced-transparency: reduce)": {
                    background: isLight ? "rgba(242,238,250,0.97)" : "rgba(22,17,38,0.97)",
                    backdropFilter: "none",
                    WebkitBackdropFilter: "none",
                  },
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography sx={LABEL_SX}>{t("statAnswerRate")}</Typography>
                    <Box sx={{ width: 30, height: 30, borderRadius: 1.5,
                               background: alpha(rateColor, 0.1),
                               display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <CheckCircleIcon sx={{ fontSize: 15, color: rateColor }} />
                    </Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.875rem", lineHeight: 1,
                                      letterSpacing: "-0.03em", color: "text.primary" }}>
                      {answerPct}%
                    </Typography>
                    <Box sx={{ mt: 1, borderRadius: 99, height: 3,
                               bgcolor: alpha(rateColor, 0.15), overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${answerPct}%`, background: rateColor,
                                 borderRadius: 99, transition: "width .5s ease" }} />
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Box>
          {!perfLoad && perf?.lastCallAt && (
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', textAlign: 'right', mt: -1.5, mb: 2, fontVariantNumeric: 'tabular-nums' }}>
              {t("lastCall")} {dateTimeText(perf.lastCallAt)}
            </Typography>
          )}

          {/* Live call panel */}
          {activeCallInfo && (
            <LiveCallPanel
              callInfo={activeCallInfo}
              history={callerHistory}
              historyLoading={historyLoading}
              onCreateTicket={(cid) => setCreateTicket({ open: true, callerId: cid, uniqueId: "" })}
              onOpenTicket={(id) => setViewTicketId(id)}
              onProfileSaved={handleProfileSaved}
              onTicketStatusChange={updateTicketStatus}
              onNoteAdded={handleNoteAdded}
              audioRef={audioRef}
              crmContact={crmContact}
              crmContactLoading={crmContactLoading}
              oncallPerm={oncallPerm}
              handoff={handoff}
              briefing={briefing}
              onCrmContactCreated={(c) => setCrmContact(c)}
            />
          )}

          {/* Calls / Tickets table card */}
          <Box sx={{
            borderRadius: "16px", overflow: "hidden",
            background: "var(--surface-card)",
            
            
            border: "1px solid var(--line-200)",
            boxShadow: "var(--shadow-card)",
            "@media (prefers-reduced-transparency: reduce)": {
              background: isLight ? "rgba(242,238,250,0.97)" : "rgba(22,17,38,0.97)",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
            },
          }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}
              TabIndicatorProps={{ style: { background: BRAND, height: 2 } }}
              sx={{
                borderBottom: `1px solid rgba(197,189,212,0.4)`, px: 1,
                "& .MuiTab-root": {
                  fontWeight: 500, fontSize: "0.875rem", minWidth: 0, px: 2,
                  textTransform: "none", color: "text.secondary",
                  "&.Mui-selected": { color: BRAND, fontWeight: 700 },
                },
              }}>
              <Tab label={t("tabAllAttempts")} />
              <Tab label={t("tabAnsweredCalls")} />
              <Tab label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {t("tabMyTickets")}
                  {openTicketCount > 0 && (
                    <Box sx={{ minWidth: 18, height: 18, borderRadius: "9px", px: 0.625,
                               background: BRAND, color: "#fff",
                               display: "flex", alignItems: "center", justifyContent: "center",
                               fontSize: "0.65rem", fontWeight: 700 }}>
                      {openTicketCount}
                    </Box>
                  )}
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {t("tabCallbacks")}
                  {callbacks.length > 0 && (
                    <Box sx={{ minWidth: 18, height: 18, borderRadius: "9px", px: 0.625,
                               background: "#D97706", color: "#fff",
                               display: "flex", alignItems: "center", justifyContent: "center",
                               fontSize: "0.65rem", fontWeight: 700 }}>
                      {callbacks.length}
                    </Box>
                  )}
                </Box>
              } />
            </Tabs>

            {/* ── Attempts / Answered tables ────────────────────────────── */}
            {tab < 2 && (
              callsLoad ? (
                <Box sx={{ p: 5, textAlign: "center" }}>
                  <CircularProgress size={22} sx={{ color: BRAND }} />
                </Box>
              ) : calls.length === 0 ? (
                <EmptyState
                  icon={<PhoneOutlinedIcon />}
                  title={tab === 0 ? t("emptyCallAttempts") : t("emptyAnsweredCalls")}
                  description={`${fromDate}${fromDate !== toDate ? ` – ${toDate}` : ""}`}
                  size="sm"
                  inline
                />
              ) : (
                <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TH>{t("colTime")}</TH>
                        <TH>{t("colCaller")}</TH>
                        <TH>{t("colSection")}</TH>
                        {tab === 0 && <TH>{t("colStatus")}</TH>}
                        <TH>{t("colDuration")}</TH>
                        <TH>{t("colRec")}</TH>
                        <TH></TH>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {calls.slice(callsPage * ROWS_PER_PAGE, (callsPage + 1) * ROWS_PER_PAGE).map((row, i) => {
                        const time     = (tab === 0 ? row.attemptTime : row.callDate) || "";
                        const callerId = row.CallerId || row.callerId || "-";
                        const section  = row.SectionName || row.sectionName || "-";
                        const lang     = row.SelectedLanguage || row.selectedLanguage || null;
                        const uid      = row.UniqueId || row.uniqueId;
                        const dialSt      = row.DialStatus || row.dialStatus;
                        const dialAnswered = dialSt?.toUpperCase() === "ANSWER" ||
                                             dialSt?.toUpperCase() === "ANSWERED";
                        const hasRec   = !!(row.RecordingPath || row.recordingPath) &&
                                         (tab === 1 || dialAnswered);
                        return (
                          <TableRow key={`${uid}-${i}`}
                            sx={{ "&:last-child td": { border: 0 },
                                  "&:hover td": { background: (theme) => alpha(theme.palette.text.primary, 0.025) } }}>
                            <TableCell sx={{ fontSize: "0.78rem", whiteSpace: "nowrap", px: 1.75,
                                             fontFamily: "monospace", color: "text.secondary" }}>
                              {time ? bizDayjs(time).format("HH:mm") : ""}
                            </TableCell>
                            <TableCell sx={{ px: 1.75 }}>
                              {row.callerName && (
                                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, lineHeight: 1.2, color: "text.primary" }}>
                                  {row.callerName}
                                </Typography>
                              )}
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography sx={{ fontSize: row.callerName ? "0.72rem" : "0.8rem", fontWeight: row.callerName ? 400 : 600, color: row.callerName ? "text.disabled" : "text.primary", fontVariantNumeric: "tabular-nums" }}>
                                  {callerId}
                                </Typography>
                                <CallButton number={callerId} name={row.callerName || ""} sx={{ width: 28, height: 28 }} />
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.78rem", color: "text.secondary", px: 1.75 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span>{section}</span>
                                {lang && (
                                  <Chip
                                    label={lang.slice(0, 2).toUpperCase()}
                                    size="small"
                                    sx={{ height: 14, fontSize: '0.5rem', fontWeight: 700, color: '#06B6D4', background: alpha('#06B6D4', 0.08), border: `1px solid ${alpha('#06B6D4', 0.2)}`, '& .MuiChip-label': { px: 0.5 } }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            {tab === 0 && (
                              <TableCell sx={{ px: 1.75 }}>
                                <StatusChip st={dialSt} />
                              </TableCell>
                            )}
                            <TableCell sx={{ fontSize: "0.78rem", color: "text.secondary", px: 1.75 }}>
                              {fmtSecs(row.Duration || row.duration)}
                            </TableCell>
                            <TableCell sx={{ px: 1.75 }}>
                              <RecordingCell uniqueId={uid} hasRecording={hasRec} audioRef={audioRef} />
                            </TableCell>
                            <TableCell sx={{ px: 1.75 }}>
                              <Tooltip title={t("tooltipCreateTicket")}>
                                <IconButton size="small"
                                  onClick={() => setCreateTicket({ open: true, callerId, uniqueId: uid })}
                                  sx={{ color: BRAND, "&:hover": { background: alpha(BRAND, 0.06) } }}>
                                  <ConfirmationNumberOutlinedIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {calls.length > ROWS_PER_PAGE && (
                  <TablePagination
                    component="div"
                    count={calls.length}
                    page={callsPage}
                    rowsPerPage={ROWS_PER_PAGE}
                    rowsPerPageOptions={[]}
                    onPageChange={(_, p) => setCallsPage(p)}
                    sx={{ borderTop: "1px solid rgba(197,189,212,0.4)" }}
                  />
                )}
                </>
              )
            )}

            {/* ── Tickets tab ──────────────────────────────────────────── */}
            {tab === 2 && (
              <>
                <Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center",
                           justifyContent: "space-between",
                           borderBottom: `1px solid rgba(197,189,212,0.4)` }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                    {t("myTickets")}
                    {openTicketCount > 0 && (
                      <Box component="span" sx={{ ml: 1, px: 0.875, py: 0.15, borderRadius: 1,
                                                   background: alpha(BRAND, 0.08), color: BRAND,
                                                   fontSize: "0.72rem", fontWeight: 700 }}>
                        {t("ticketsOpen", { count: openTicketCount })}
                      </Box>
                    )}
                  </Typography>
                  <Button size="small" variant="outlined"
                    startIcon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: 15 }} />}
                    onClick={() => setCreateTicket({ open: true, callerId: "", uniqueId: "" })}
                    sx={{ fontWeight: 700, fontSize: "0.78rem", height: 30,
                          borderColor: BRAND, color: BRAND,
                          "&:hover": { background: alpha(BRAND, 0.05), borderColor: BRAND } }}>
                    {t("ticketsNewTicket")}
                  </Button>
                </Box>

                {tickLoad ? (
                  <Box sx={{ p: 5, textAlign: "center" }}>
                    <CircularProgress size={22} sx={{ color: BRAND }} />
                  </Box>
                ) : tickets.length === 0 ? (
                  <EmptyState
                    icon={<AssignmentOutlinedIcon />}
                    title={t("ticketsNoTickets")}
                    description={t("ticketsNoTicketsDesc")}
                    size="sm"
                    inline
                  />
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TH sx={{ width: 52 }}>#</TH>
                          <TH>{t("colSubject")}</TH>
                          <TH>{t("colCaller")}</TH>
                          <TH>{t("colStatus")}</TH>
                          <TH>{t("colPriority")}</TH>
                          <TH>{t("colCreated")}</TH>
                          <TH></TH>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tickets.map((tk) => (
                          <TableRow key={tk.id} onClick={() => setViewTicketId(tk.id)}
                            sx={{ cursor: "pointer", "&:last-child td": { border: 0 },
                                  "&:hover td": { background: (th) => alpha(th.palette.text.primary, 0.025) } }}>
                            <TableCell sx={{ fontSize: "0.78rem", color: "text.disabled",
                                             fontFamily: "monospace", px: 1.75 }}>
                              #{tk.id}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600, px: 1.75 }}>
                              {tk.subject}
                            </TableCell>
                            <TableCell sx={{ px: 1.75, whiteSpace: 'nowrap' }}>
                              <Box>
                                {tk.callerName && (
                                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                                    {tk.callerName}
                                  </Typography>
                                )}
                                <Typography sx={{ fontSize: tk.callerName ? '0.68rem' : '0.78rem', color: tk.callerName ? 'text.disabled' : 'text.secondary', fontFamily: 'monospace' }}>
                                  {tk.callerId || '—'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ px: 1.75 }}
                              onClick={e => { e.stopPropagation();
                                setStatusMenu({ anchorEl: e.currentTarget, ticketId: tk.id }); }}>
                              <TicketStatusChip status={tk.status} />
                            </TableCell>
                            <TableCell sx={{ px: 1.75 }}>
                              <PriorityChip priority={tk.priority} />
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.75rem", color: "text.secondary",
                                             whiteSpace: "nowrap", px: 1.75, fontFamily: "monospace" }}>
                              {tk.createdAt ? bizDayjs(tk.createdAt).format("YYYY-MM-DD") : ""}
                            </TableCell>
                            <TableCell sx={{ px: 1.75 }}>
                              <Tooltip title={t("tooltipOpenTicket")}>
                                <IconButton size="small"
                                  onClick={e => { e.stopPropagation(); setViewTicketId(tk.id); }}
                                  sx={{ color: BRAND, "&:hover": { background: alpha(BRAND, 0.06) } }}>
                                  <ConfirmationNumberOutlinedIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {/* ── Callbacks tab ─────────────────────────────────────────── */}
            {tab === 3 && (
              <Box sx={{ p: 2 }}>
                {/* Add callback form toggle */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
                  <Box onClick={() => setCbFormOpen((o) => !o)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.75,
                      px: 1.5, py: 0.625, borderRadius: "10px", cursor: "pointer",
                      background: cbFormOpen ? alpha(BRAND, 0.1) : "var(--surface-card)",
                      border: `1px solid ${cbFormOpen ? alpha(BRAND, 0.3) : "var(--line-100)"}`,
                      boxShadow: "var(--shadow-sm)",
                      color: cbFormOpen ? BRAND : "text.secondary",
                      fontSize: "0.8rem", fontWeight: 600,
                      transition: "all 0.15s",
                    }}>
                    <TaskAltOutlinedIcon sx={{ fontSize: 15 }} />
                    {cbFormOpen ? t("cbCancel") : t("cbSchedule")}
                  </Box>
                </Box>

                {/* Form */}
                {cbFormOpen && (
                  <Box sx={{
                    mb: 2, p: 2, borderRadius: "14px",
                    background: "var(--surface-2)",
                    border: "1px solid var(--line-100)",
                  }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.5 }}>
                      <TextField size="small" label={t("cbPhoneLabel")} value={cbForm.callerNum}
                        onChange={(e) => setCbForm((f) => ({ ...f, callerNum: e.target.value }))} />
                      <TextField size="small" label={t("cbNameLabel")} value={cbForm.callerName}
                        onChange={(e) => setCbForm((f) => ({ ...f, callerName: e.target.value }))} />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 1.5, mb: 1.5 }}>
                      <TextField size="small" label={t("cbDateTimeLabel")} type="datetime-local"
                        value={cbForm.scheduledAt}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setCbForm((f) => ({ ...f, scheduledAt: e.target.value }))} />
                    </Box>
                    <TextField size="small" fullWidth label={t("cbNotesLabel")} value={cbForm.notes} multiline rows={2}
                      onChange={(e) => setCbForm((f) => ({ ...f, notes: e.target.value }))}
                      sx={{ mb: 1.5 }} />
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Box component="button" onClick={createCallback} disabled={cbSaving || !cbForm.callerNum || !cbForm.scheduledAt}
                        sx={{
                          px: 2, py: 0.75, borderRadius: "10px", cursor: "pointer", border: "none",
                          background: BRAND, color: "#fff", fontWeight: 700, fontSize: "0.82rem",
                          opacity: (cbSaving || !cbForm.callerNum || !cbForm.scheduledAt) ? 0.5 : 1,
                          fontFamily: "inherit",
                        }}>
                        {cbSaving ? t("cbSaving") : t("cbSave")}
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* List */}
                {cbLoading ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress size={22} sx={{ color: BRAND }} />
                  </Box>
                ) : callbacks.length === 0 ? (
                  <EmptyState icon={<TaskAltOutlinedIcon />}
                    title={t("cbNoPending")}
                    description={t("cbNoPendingDesc")}
                    size="sm" inline />
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {callbacks.map((cb) => {
                      const isOverdue = new Date(cb.scheduledAt) < new Date();
                      return (
                        <Box key={cb.id} sx={{
                          display: "flex", gap: 1.5, alignItems: "flex-start",
                          p: 1.75, borderRadius: "12px",
                          background: "var(--surface-card)",
                          border: `1px solid ${isOverdue ? alpha("#EF4444", 0.3) : "var(--line-100)"}`,
                          boxShadow: "var(--shadow-sm)",
                        }}>
                          <Box sx={{
                            width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                            background: alpha(BRAND, 0.08),
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            <PhoneOutlinedIcon sx={{ fontSize: 16, color: BRAND }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "text.primary" }}>
                              {cb.callerName || cb.callerNum}
                            </Typography>
                            {cb.callerName && (
                              <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", fontFamily: "monospace" }}>
                                {cb.callerNum}
                              </Typography>
                            )}
                            <Typography sx={{ fontSize: "0.72rem", color: isOverdue ? "#DC2626" : "text.secondary", mt: 0.25 }}>
                              {isOverdue ? `⚠ ${t("cbOverdue")} · ` : ""}{dateTimeText(cb.scheduledAt)}
                            </Typography>
                            <CallButton variant="button" number={cb.callerNum} name={cb.callerName || ""} sx={{ mt: 0.75, minHeight: 32 }} />
                            {cb.notes && (
                              <Typography sx={{ fontSize: "0.72rem", color: "text.disabled", mt: 0.25, fontStyle: "italic" }}>
                                {cb.notes}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Tooltip title={t("cbMarkDone")}>
                              <IconButton size="small" onClick={() => doneCallback(cb.id)}
                                sx={{ color: "#059669", "&:hover": { background: alpha("#22C55E", 0.08) } }}>
                                <TaskAltOutlinedIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("cbDelete")}>
                              <IconButton size="small" onClick={() => deleteCallback(cb.id)}
                                sx={{ color: "text.disabled", "&:hover": { color: "#DC2626" } }}>
                                <CloseIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
        {/* ── End left column ─────────────────────────────────────────── */}

        {/* The phone is docked beside every page now (PhoneProvider.jsx, 4.8f.4b) — no column of its own here. */}
      </Box>

      {/* hidden audio */}
      <audio ref={audioRef} style={{ display: "none" }} />

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}
      <CreateTicketDialog
        open={createTicket.open}
        onClose={() => setCreateTicket((s) => ({ ...s, open: false }))}
        onCreated={(ticket) => {
          setTickets((prev) => [ticket, ...prev]);
          setCreateTicket((s) => ({ ...s, open: false }));
          if (callerHistory?.callerId === ticket.callerId) loadCallerHistory(ticket.callerId);
          setTab(2);
          setViewTicketId(ticket.id);
        }}
        prefillCallerId={createTicket.callerId}
        prefillUniqueId={createTicket.uniqueId}
      />

      <TicketDetailDialog
        open={!!viewTicketId}
        ticketId={viewTicketId}
        onClose={() => setViewTicketId(null)}
        team={team}
        currentUser={user}
      />

      {/* Inline status update menu */}
      <Menu
        anchorEl={statusMenu.anchorEl}
        open={Boolean(statusMenu.anchorEl)}
        onClose={() => setStatusMenu({ anchorEl: null, ticketId: null })}
        PaperProps={{ sx: {
          borderRadius: "14px",
          background: "var(--surface-card)",
          
          
          border: "1px solid var(--line-200)",
          boxShadow: "var(--shadow-card)",
          minWidth: 148,
          "@media (prefers-reduced-transparency: reduce)": {
            background: isLight ? "rgba(242,238,250,0.97)" : "rgba(22,17,38,0.97)",
            backdropFilter: "none",
            WebkitBackdropFilter: "none",
          },
        } }}>
        {["open","in_progress","resolved","closed"].map((s) => {
          const sc = TICKET_STATUS_COLORS[s] || {};
          return (
            <MenuItem key={s} onClick={() => updateTicketStatus(statusMenu.ticketId, s)}
              sx={{ fontSize: "0.82rem", gap: 1, py: 0.875,
                    "&:hover": { background: alpha(sc.color || BRAND, 0.06) } }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%",
                         background: sc.color || "#999", flexShrink: 0 }} />
              <span style={{ textTransform: "capitalize" }}>{s.replace("_", " ")}</span>
            </MenuItem>
          );
        })}
      </Menu>

      {/* ── Post-call Wrap-up Dialog ───────────────────────────────────────── */}
      <Dialog
        open={!!dispDialog}
        onClose={() => !dispSaving && (setDispDialog(null), setDispNote(""))}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: {
          borderRadius: "18px",
          background: "var(--surface-card)",
          border: "1px solid var(--line-200)",
          boxShadow: "var(--shadow-card)",
        }}}
      >
        <DialogTitle sx={{ pb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
          <LabelOutlinedIcon sx={{ fontSize: 20, color: BRAND }} />
          <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>{t("wrapUpTitle")}</Typography>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <TimerOutlinedIcon sx={{ fontSize: 13, color: "text.disabled" }} />
            <PostCallCountdown active={!!dispDialog} onExpire={() => { setDispDialog(null); setDispNote(""); }} />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", mb: 2 }}>
            {t("wrapUpCaller")} <strong>{dispDialog?.caller}</strong>
            {dispDialog?.duration > 0 && (
              <> &mdash; {Math.floor(dispDialog.duration / 60)}:{String(dispDialog.duration % 60).padStart(2, "0")}</>
            )}
          </Typography>

          {/* Dispositions */}
          {dispositions.length === 0 ? (
            <Typography sx={{ fontSize: "0.82rem", color: "text.disabled", textAlign: "center", py: 1 }}>
              {t("wrapUpNoDispositions")}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {dispositions.filter(d => d.isActive !== 0).map(d => (
                <Box
                  key={d.id}
                  component="button"
                  onClick={() => setDispSelected(dispSelected === d.id ? "" : d.id)}
                  sx={{
                    px: 1.5, py: 0.625,
                    borderRadius: "20px",
                    border: `2px solid ${dispSelected === d.id ? d.color : alpha(d.color || BRAND, 0.3)}`,
                    background: dispSelected === d.id ? alpha(d.color || BRAND, 0.12) : "transparent",
                    color: d.color || BRAND,
                    fontWeight: dispSelected === d.id ? 700 : 500,
                    fontSize: "0.8125rem",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all 0.12s",
                  }}
                >
                  {d.label}
                </Box>
              ))}
            </Box>
          )}

          {/* Call note (only shown if contact is in CRM) */}
          {dispDialog?.crmContactId && (
            <Box>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "text.secondary",
                                 textTransform: "uppercase", letterSpacing: ".06em", mb: 0.75 }}>
                {t("wrapUpNoteLabel")}
              </Typography>
              <TextField
                size="small" multiline rows={3} fullWidth
                placeholder={t("wrapUpNotePlaceholder")}
                value={dispNote}
                onChange={(e) => setDispNote(e.target.value)}
                sx={{ "& textarea": { fontSize: "0.8rem" } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button
            size="small"
            onClick={() => { setDispDialog(null); setDispNote(""); }}
            disabled={dispSaving}
            sx={{ color: "text.secondary", textTransform: "none", fontWeight: 500 }}
          >
            {t("wrapUpSkip")}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleDispSave}
            disabled={dispSaving || (!dispSelected && !dispNote.trim())}
            sx={{
              textTransform: "none", fontWeight: 600, borderRadius: "8px",
              background: BRAND, "&:hover": { background: "#4338CA" },
            }}
          >
            {dispSaving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : t("wrapUpSave")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── 60-second auto-dismiss countdown for post-call dialog ─────────────────────
function PostCallCountdown({ active, onExpire }) {
  const [secs, setSecs] = useState(60);
  useEffect(() => {
    if (!active) { setSecs(60); return; }
    setSecs(60);
    const id = setInterval(() => {
      setSecs((s) => {
        if (s <= 1) { clearInterval(id); onExpire(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  return (
    <Typography sx={{ fontSize: "0.68rem", color: secs <= 10 ? "#DC2626" : "text.disabled",
                       fontFamily: "monospace", fontWeight: 600 }}>
      {secs}s
    </Typography>
  );
}
